import { useMemo, useRef, useState } from 'react'
import { LuCircleAlert, LuSearch, LuSearchX, LuTruck, LuX } from 'react-icons/lu'
import dayjs from 'dayjs'

import { toaster } from '@/components/Toaster'

import { Button } from '@/lib/core/components/Button'
import { usePopover } from '@/lib/core/hooks/usePopover'
import { Badge, BadgeVariant } from '@/lib/core/components/Badge'

import { TaskDefinition, TaskVehicleAvailabilityDefinition } from '../../types/task'
import { findTaskSchedule, TaskSchedule } from '../../utils/taskSchedule'
import styles from '../TaskCellPopout.module.css'
import { TaskStatus } from '../CreateTaskModal'
import { UpdateTaskPayload, useUpdateTaskAssignment } from '../../api'
import { useTaskVehicleAvailability } from '../../api/useTasks'
import { AdditionalAssignVehicleModal } from '../AssignAddtionalVehicleModal/AdditionalAssignVehicleModal'

interface AssignedVehiclePopoutProps {
  task: TaskDefinition
  testId?: string
  onUpdated: () => void
}

/** Whether two schedules share any time, ends included. */
const schedulesOverlap = (a: TaskSchedule, b: TaskSchedule) =>
  dayjs(a.start).isSameOrBefore(b.end) && dayjs(a.end).isSameOrAfter(b.start)

// A task with no usable schedule cannot be compared, so it never counts as overlapping or active —
// what the old date comparisons also produced on bad data, except findTaskSchedule now reports it.

export const computeConflictTasks = (
  vehicle: TaskVehicleAvailabilityDefinition,
  task: TaskDefinition,
): TaskDefinition[] => {
  const taskSchedule = findTaskSchedule(task)
  if (!taskSchedule) return []

  return vehicle.assignedTasks.filter((assignedTask) => {
    const assignedSchedule = findTaskSchedule(assignedTask)
    return assignedSchedule != null && schedulesOverlap(assignedSchedule, taskSchedule)
  }) as TaskDefinition[]
}

export const computeActiveTask = (
  vehicle: TaskVehicleAvailabilityDefinition,
  task: TaskDefinition,
): TaskDefinition | undefined => {
  const taskSchedule = findTaskSchedule(task)
  const now = dayjs()

  const foundOverlapTask = vehicle.assignedTasks.find((assignedTask) => {
    const assignedSchedule = findTaskSchedule(assignedTask)
    if (!assignedSchedule) return false

    const isOverlapping = taskSchedule != null && schedulesOverlap(assignedSchedule, taskSchedule)
    const isCurrentlyActive =
      now.isBefore(assignedSchedule.end) && now.isAfter(assignedSchedule.start)
    const isUpcoming = now.isBefore(assignedSchedule.start)

    return isOverlapping || isCurrentlyActive || isUpcoming
  })

  return foundOverlapTask as TaskDefinition
}

export const AssignedVehiclePopout = ({ task, testId, onUpdated }: AssignedVehiclePopoutProps) => {
  const computedTestId = testId ?? `assigned-vehicle-popout-${task.taskDefinition.id}`

  const buttonRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { positionPopover } = usePopover({
    buttonRef,
    dialogRef,
    defaultPopoverDimensions: { width: 324, height: 275 },
  })
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false)
  const [selectedVehicle, setSelectedVehicle] = useState<TaskVehicleAvailabilityDefinition>()

  const { trigger: updateTaskStatus, isMutating: isTaskUpdating } = useUpdateTaskAssignment(
    task.taskDefinition.id,
    task.id,
  )

  const showPopover = (e: React.MouseEvent) => {
    e.stopPropagation()
    positionPopover()
    dialogRef.current?.showPopover()
    setSelectedVehicle(undefined)
  }

  const handleOnSelectAssignedVehicle = async (vehicle: TaskVehicleAvailabilityDefinition) => {
    const activeTask = computeActiveTask(vehicle, task)
    const overlappingTasks = computeConflictTasks(vehicle, task)
    if (activeTask || overlappingTasks.length > 0) {
      setIsOpenModal(true)
      setSelectedVehicle(vehicle)
    } else {
      await handleUpdateAssignedVehicle(vehicle.id)
    }
  }

  const handleUpdateAssignedVehicle = async (vehicleId: number) => {
    try {
      const payload: UpdateTaskPayload = { assignedAsset: vehicleId }
      await updateTaskStatus(payload)
      toaster({
        type: 'success',
        message: 'Task assigned vehicle updated',
      })
      onUpdated()
      dialogRef.current?.hidePopover()
      setIsOpenModal(false)
      await refreshTaskVehicleAvailability()
    } catch {
      toaster({
        type: 'error',
        message: 'Failed to update task assigned vehicle. Please try again.',
      })
    }
  }

  const { data: taskVehicleAvailabilityData, mutate: refreshTaskVehicleAvailability } =
    useTaskVehicleAvailability()

  const vehicles = useMemo(() => {
    return (
      taskVehicleAvailabilityData?.sort((a, b) =>
        a.plateNumber && b.plateNumber
          ? a.plateNumber.localeCompare(b.plateNumber, 'en', { caseFirst: 'upper', numeric: true })
          : 0,
      ) ?? []
    )
  }, [taskVehicleAvailabilityData])

  const searchFilteredVehicles = useMemo(() => {
    if (!searchTerm.trim()) {
      return vehicles.filter(
        (vehicle) => vehicle.id !== task.assignedVehicle?.id && vehicle.id !== selectedVehicle?.id,
      )
    }

    return vehicles.filter(
      (vehicle) =>
        vehicle.id !== task.assignedVehicle?.id &&
        vehicle.id !== selectedVehicle?.id &&
        vehicle.plateNumber?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
  }, [vehicles, searchTerm, selectedVehicle, task])

  const isDisabled = useMemo(() => {
    return (
      isTaskUpdating ||
      [TaskStatus.COMPLETED, TaskStatus.REJECTED, TaskStatus.CANCELLED].includes(
        task.status as TaskStatus,
      ) ||
      !!task.onwardAssignedToOrg
    )
  }, [task, isTaskUpdating])

  const isActiveTask = (vehicle: TaskVehicleAvailabilityDefinition): boolean => {
    return !!computeActiveTask(vehicle, task)
  }

  return (
    <div data-testid={`${computedTestId}-container`}>
      <div className={`${styles.taskCellPopout}`.trim()} data-testid={computedTestId}>
        <Button
          id={`assigned-company-dialog-${task.taskDefinition.id}`}
          ref={buttonRef}
          variant="ghost"
          leftIcon={<LuTruck size={16} />}
          rightIcon={!task.assignedVehicle && <LuCircleAlert size={16} color="#F68D1C" />}
          className={styles.taskCellPopoutButton}
          onClick={showPopover}
          disabled={isDisabled}
          data-testid={`${computedTestId}-trigger-button`}
        >
          <div className={styles.taskCellPopoutButtonLabel}>
            {task.assignedVehicle ? task.assignedVehicle.plateNumber : 'UNASSIGNED'}
          </div>
        </Button>

        {selectedVehicle && (
          <AdditionalAssignVehicleModal
            isOpen={isOpenModal}
            task={task}
            selectedVehicle={selectedVehicle}
            testId={computedTestId}
            onClose={() => setIsOpenModal(false)}
            onAssign={(vehicleId) => handleUpdateAssignedVehicle(vehicleId)}
          />
        )}

        <dialog
          id={`assigned-vehicle-dialog-${task.taskDefinition.id}`}
          ref={dialogRef}
          className={styles.popoutContainer}
          popover="auto"
        >
          <div className={styles.popoutWrapper}>
            <div className={styles.popoutHeader}>
              <div className={styles.searchContainer}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search Vehicle"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                  data-testid={`assigned-vehicle-dialog-${task.taskDefinition.id}-search-field`}
                />
                <button
                  className={styles.searchIconButton}
                  data-testid={`assigned-vehicle-dialog-${task.taskDefinition.id}-search-icon`}
                  onClick={() => {
                    if (searchTerm) {
                      setSearchTerm('')
                    }
                    searchInputRef.current?.focus()
                  }}
                >
                  {searchTerm ? <LuSearchX size={18} /> : <LuSearch size={18} />}
                </button>
              </div>
              <div className={styles.headerActions}>
                <button
                  className={styles.actionButton}
                  data-testid={`assigned-vehicle-dialog-${task.taskDefinition.id}-close`}
                  onClick={() => {
                    setSearchTerm('')
                    dialogRef.current?.hidePopover()
                  }}
                >
                  <LuX size={16} />
                </button>
              </div>
            </div>

            <div className={styles.optionsWrapper}>
              {searchFilteredVehicles.map((vehicle) => (
                <div
                  key={`vehicle-option-${vehicle.id}`}
                  onClick={() => handleOnSelectAssignedVehicle(vehicle)}
                  className={styles.option}
                >
                  <span>{vehicle.plateNumber}</span>
                  <Badge
                    minSize={22}
                    variant={BadgeVariant.VEHICLE}
                    backgroundColor={isActiveTask(vehicle) ? '#F0FFF2' : '#EBF9FF'}
                    textColor="#747474"
                  >
                    {isActiveTask(vehicle) ? 'ACTIVE TASK' : 'AVAILABLE'}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </dialog>
      </div>
    </div>
  )
}
